import React from "react";
import ShakaPlayer from "shaka-player-react";
import "shaka-player/dist/controls.css";
import PropTypes from "prop-types";
import shaka from "shaka-player/dist/shaka-player.ui.js";
import { fnBrowserDetect } from "utilFunctions/helperFunctions/utilFunctions";

function VideoPlayer({ videoUrl, JWTToken, assetId, source }) {
  const videoRef = React.useRef(null);
  const videoContainerRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const currentErrCountRef = React.useRef(0);
  const contentUriRef = React.useRef(videoUrl);

  const [isAssetPlaying, setIsAssetPlaying] = React.useState(true);

  // Function to retry loading the video
  const playVideo = async () => {
    if (playerRef.current) {
      try {
        await playerRef.current.load(contentUriRef.current);
        console.log("Retry load succeeded");
      } catch (error) {
        console.error("Retry load failed", error);
      }
    }
  };

  // Helper to return the player instance
  const getVideoElements = () => {
    return { player: playerRef.current };
  };

  // List of error codes for transmux errors
  const transmuxErrorCodes = [
    shaka.util.Error.Code.MEDIA_SOURCE_OPERATION_FAILED,
    shaka.util.Error.Code.VIDEO_ERROR,
  ];

  // The new error handler function
  const shakaPlayerErrorHandler = async (err) => {
    currentErrCountRef.current += 1;
    console.error("Shaka error code:", err.code, "object", err);

    if (err.code === shaka.util.Error.Code.TRANSMUXING_FAILED) {
      const { player } = getVideoElements();
      player.configure("manifest.disableAudio", true);
      console.log("Only video stream is available. Retrying playing.");
      await playVideo();
      return;
    }

    if (transmuxErrorCodes.includes(err.code)) {
      if (currentErrCountRef.current > 3) return;
      const { player } = getVideoElements();
      // In Safari, if video starts from > 0, forcetransmux may be needed
      player?.configure("mediaSource.forceTransmux", true);
      await playVideo();
      return;
    }

    const requestErrorType = (err.data ?? [])[err.data?.length - 1];
    if (requestErrorType === shaka.net.NetworkingEngine.RequestType.KEY) {
      err.code;
      setIsAssetPlaying(false);
      return;
    }

    if (err.severity === shaka.util.Error.Severity.CRITICAL) {
      err.code;
      setIsAssetPlaying(false);
      return;
    }
  };

  // Replace the default error event with our new handler
  const onErrorEventHandler = (event) => {
    shakaPlayerErrorHandler(event.detail);
  };

  React.useEffect(() => {
    function initApp() {
      shaka.polyfill.installAll();
      if (shaka.Player.isBrowserSupported()) {
        initPlayer(fnBrowserDetect());
      } else {
        console.error("Browser not supported!");
      }
    }
    initApp();
  }, []);

  function initPlayer(browser) {
    const playerConfig = {
      preferredTextLanguage: "en",
      preferredTextRole: "chapters",
      streaming: {
        useNativeHlsOnSafari: false,
        alwaysStreamText: true,
      },
    };

    const videoContainer = videoContainerRef.current;
    const video = document.getElementById("my-player");
    const player = new shaka.Player(video);
    playerRef.current = player;
    window.player = player;

    // Set up Shaka UI overlay
    // const uiConfig = {
    //   controlPanelElements: [
    //     "play_pause",
    //     "mute",
    //     "volume",
    //     "time_and_duration",
    //     "fullscreen",
    //     "overflow_menu",
    //   ],
    // };
    const ui = new shaka.ui.Overlay(player, videoContainer, video);
    //ui.configure(uiConfig); // Uncomment if you need to apply configuration
    ui.getControls();

    // Use our new error handler here
    player.addEventListener("error", onErrorEventHandler);
    console.log("browser71", browser, isAssetPlaying);

    // Register request filters to modify DRM and KEY requests
    player.getNetworkingEngine().registerRequestFilter((type, request) => {
      if (type === shaka.net.NetworkingEngine.RequestType.LICENSE) {
        if (request.uris) {
          request.uris[0] =
            request.uris[0] +
            `&source-id=cms-preview&authorization=${JWTToken}`;
        }
      }
      if (type === shaka.net.NetworkingEngine.RequestType.KEY) {
        const extraHeaders = {
          assetId: assetId,
          identity: "CMS-preview",
          source: source ? source : "",
        };
        for (const key in extraHeaders) {
          if (Object.hasOwnProperty.call(extraHeaders, key)) {
            request.headers[key] = extraHeaders[key];
          }
        }
      }
    });

    player.setTextTrackVisibility(true);

    // Load the video
    player
      .load(contentUriRef.current)
      .then(() => {
        console.log("The video has now been loaded!");
      })
      .catch((e) => {
        console.error("Error loading video", e);
      });

    player.configure(playerConfig);
  }

  return (
    <div className="video-container" ref={videoContainerRef}>
      <ShakaPlayer
        id="my-player"
        className="shaka-video"
        ref={videoRef}
        autoPlay={false}
      />
    </div>
  );
}

VideoPlayer.propTypes = {
  videoUrl: PropTypes.string,
  JWTToken: PropTypes.string,
  assetId: PropTypes.any,
  source: PropTypes.string,
};

export default VideoPlayer;
